import type { FindriscAnswers, FindriscResults } from '../types/findrisc';
import { getScoreForQuestion } from './loinc-questionnaire';

export const calculateRisk = (answers: FindriscAnswers): FindriscResults => {
  let totalScore = 0;

  // Age scoring using LOINC LA codes
  if (answers.age === 'LA31987-3') totalScore += 0; // Under 45 years
  else if (answers.age === 'LA31988-1') totalScore += 2; // 45-54 years
  else if (answers.age === 'LA31989-9') totalScore += 3; // 55-64 years
  else if (answers.age === 'LA31990-7') totalScore += 4; // Over 64 years

  // BMI scoring using calculated BMI
  if (answers.bmi) {
    if (answers.bmi < 25) totalScore += 0;
    else if (answers.bmi < 30) totalScore += 1;
    else totalScore += 3;
  }

  // Waist circumference scoring using LOINC LA codes
  // Check both male and female waist circumference answers
  const waistAnswer = answers.waistCircumference || answers.waistCircumferenceFemale;
  if (waistAnswer) {
    if (waistAnswer === 'LA31920-4') totalScore += 0; // Men: less than 94 cm
    else if (waistAnswer === 'LA31921-2') totalScore += 3; // Men: 94-102 cm
    else if (waistAnswer === 'LA31922-0') totalScore += 4; // Men: more than 102 cm
    else if (waistAnswer === 'LA31923-8') totalScore += 0; // Women: less than 80 cm
    else if (waistAnswer === 'LA31924-6') totalScore += 3; // Women: 80-88 cm
    else if (waistAnswer === 'LA31925-3') totalScore += 4; // Women: more than 88 cm
  }

  // Physical activity scoring using LOINC LA codes
  if (answers.physicalActivity === 'LA33-6') totalScore += 0; // Yes
  else if (answers.physicalActivity === 'LA32-8') totalScore += 2; // No

  // Vegetables/fruits scoring using LOINC LA codes
  if (answers.vegetablesFruits === 'LA14799-3') totalScore += 0; // Every day
  else if (answers.vegetablesFruits === 'LA31926-1') totalScore += 1; // Not every day

  // Blood pressure medication scoring using LOINC LA codes
  if (answers.bloodPressureMeds === 'LA32-8') totalScore += 0; // No
  else if (answers.bloodPressureMeds === 'LA33-6') totalScore += 2; // Yes

  // High blood glucose scoring using LOINC LA codes
  if (answers.highBloodGlucose === 'LA32-8') totalScore += 0; // No
  else if (answers.highBloodGlucose === 'LA33-6') totalScore += 5; // Yes

  // Family history scoring using LOINC LA codes
  if (answers.familyHistory === 'LA32-8') totalScore += 0; // No
  else if (answers.familyHistory === 'LA31927-9') totalScore += 3; // Grandparent etc
  else if (answers.familyHistory === 'LA31928-7') totalScore += 5; // Parent etc

  // Determine risk category and percentage
  let riskCategory: 'low-moderate' | 'high' | 'very-high';
  let riskPercentage: string;
  let interpretation: string;

  if (totalScore <= 14) {
    riskCategory = 'low-moderate';
    riskPercentage = '1-17%';
    interpretation = 'Your risk of developing type 2 diabetes in the next 10 years is low to moderate. Continue maintaining healthy lifestyle habits.';
  } else if (totalScore <= 20) {
    riskCategory = 'high';
    riskPercentage = '33%';
    interpretation = 'Your risk of developing type 2 diabetes in the next 10 years is high. Consider consulting with a healthcare provider about prevention strategies.';
  } else {
    riskCategory = 'very-high';
    riskPercentage = '50%';
    interpretation = 'Your risk of developing type 2 diabetes in the next 10 years is very high. We strongly recommend consulting with a healthcare provider about prevention and screening.';
  }

  return {
    totalScore,
    riskCategory,
    riskPercentage,
    interpretation
  };
};